import { useState, useEffect } from 'react';
import {
  Stack,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Button,
  Group,
  Alert,
  Grid
} from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useApiClient } from '../../api';

// Renders an ApiError: 422 validation errors become "field: message" lines.
function formatApiError(err) {
  if (Array.isArray(err?.errors) && err.errors.length > 0) {
    return err.errors.map((e) => `${e.field}: ${e.message}`).join('\n');
  }
  return err?.message || 'Request failed';
}

const ProductForm = ({ product, isEditing, onSave, onCancel }) => {
  const api = useApiClient();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '', // dollars in the UI; converted to price_cents on submit
    color: '',
    tags: '', // comma-separated in the UI; converted to [string] on submit
    status: 'draft'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditing && product) {
      setFormData({
        title: product.title || '',
        description: product.description || '',
        price: product.price_cents != null ? product.price_cents / 100 : '',
        color: product.color || '',
        tags: Array.isArray(product.tags) ? product.tags.join(', ') : '',
        status: product.status || 'draft'
      });
    }
  }, [isEditing, product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        title: formData.title,
        description: formData.description,
        price_cents: Math.round(Number(formData.price) * 100),
        color: formData.color || null,
        tags: formData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        status: formData.status
      };

      if (isEditing) {
        await api.patch(`/api/v1/me/seller/products/${product.id}`, submitData);
      } else {
        await api.post('/api/v1/me/seller/products', submitData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving product:', err);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'out_of_stock', label: 'Out of Stock' },
    { value: 'discontinued', label: 'Discontinued' }
  ];

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {error && (
          <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', margin: 0 }}>{error}</pre>
          </Alert>
        )}

        {/* Basic Information */}
        <TextInput
          label="Product Title"
          placeholder="Enter product title"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.currentTarget.value)}
          required
        />

        <Textarea
          label="Description"
          placeholder="Describe your product in detail"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.currentTarget.value)}
          minRows={4}
          required
        />

        <Grid>
          <Grid.Col span={6}>
            <NumberInput
              label="Price ($)"
              placeholder="0.00"
              value={formData.price}
              onChange={(value) => handleInputChange('price', value)}
              min={0}
              step={0.01}
              decimalScale={2}
              required
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Color"
              placeholder="Product color"
              value={formData.color}
              onChange={(e) => handleInputChange('color', e.currentTarget.value)}
            />
          </Grid.Col>
        </Grid>

        <TextInput
          label="Tags"
          placeholder="Comma-separated tags for search"
          value={formData.tags}
          onChange={(e) => handleInputChange('tags', e.currentTarget.value)}
          description="Use tags to help customers find your product (e.g., electronics, gaming, vintage)"
        />

        {isEditing && (
          <Select
            label="Status"
            placeholder="Select status"
            value={formData.status}
            onChange={(value) => handleInputChange('status', value)}
            data={statusOptions}
            description="Draft products are not visible to customers. Active products are listed publicly."
          />
        )}

        {/* Action Buttons */}
        <Group align="right" mt="md">
          <Button
            variant="light"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
          >
            {isEditing ? 'Update Product' : 'Create Product'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default ProductForm;
